export const pinchZoom = (container) => {
  const image = container.querySelector('img')
  if (!image) return

  let minScale = 1
  let maxScale = 5

  let imageScale = 1
  let initialDistance = 0
  let initialScale = 1

  let deltaX = 0
  let deltaY = 0
  let initialX = 0
  let initialY = 0

  let panning = false
  let panningTime = 25
  let panningId = null

  let prevent = false
  let preventTime = 500
  let preventId = null

  let boxRect = null
  let imageRatio = 1

  // Calculate distance between two fingers
  const distance = (event) => {
    return Math.hypot(
      event.touches[0].pageX - event.touches[1].pageX,
      event.touches[0].pageY - event.touches[1].pageY
    )
  }

  // Transform the image to make it grow and move with fingers
  const transformImage = () => {
    const transform = `translate3d(${deltaX}px, ${deltaY}px, 0) scale(${imageScale})`

    image.style.transform = transform
    image.style.WebkitTransform = transform

    prevent = true
    clearTimeout(preventId)

    preventId = setTimeout(() => prevent = false, preventTime)
  }

  const getImageRect = () => {
    // Get the bounding rect of the image
    const irect = image.getBoundingClientRect()

    // Return bounding box if natural ratio matches node ratio
    if (imageRatio === irect.width / irect.height) {
      return irect
    }

    let scaledW = irect.width
    let scaledH = irect.height

    if (irect.width / imageRatio <= irect.height) {
      scaledH = irect.width / imageRatio
    } else {
      scaledW = irect.height * imageRatio
    }

    // Calculate position based on object-position: center
    const offsetX = (irect.width - scaledW) / 2
    const offsetY = (irect.height - scaledH) / 2

    const rectX = irect.left + offsetX
    const rectY = irect.top + offsetY

    return {
      left:   rectX,
      top:    rectY,
      width:  scaledW,
      height: scaledH,
      right:  rectX + scaledW,
      bottom: rectY + scaledH
    }
  }

  const correctPosition = () => {
    // Get the bounding rect of the image
    const irect = getImageRect()

    const prevX = deltaX
    const prevY = deltaY

    // Correct the X delta if image out of bounds
    if (irect.width < boxRect.width) {
      deltaX = 0
    } else if (irect.left > boxRect.left) {
      deltaX -= irect.left - boxRect.left
    } else if (irect.right < boxRect.right) {
      deltaX -= irect.right - boxRect.right
    }

    // Correct the Y delta if image out of bounds
    if (irect.height < boxRect.height) {
      deltaY = 0
    } else if (irect.top > boxRect.top) {
      deltaY -= irect.top - boxRect.top
    } else if (irect.bottom < boxRect.bottom) {
      deltaY -= irect.bottom - boxRect.bottom
    }

    // Re-transform the image if delta changed
    if (prevX !== deltaX || prevY !== deltaY) {
      transformImage()
    }
  }

  const onTouchStart = (event) => {
    event.preventDefault()

    boxRect = container.getBoundingClientRect()
    imageRatio = image.naturalWidth / image.naturalHeight

    if (event.touches.length === 2) {
      panning = true
      event.stopPropagation()

      // Calculate initial distance and scale for both zooming in and out
      initialDistance = distance(event)
      initialScale = imageScale

      // Calculate where the fingers have started on the X and Y axis
      initialX = ((event.touches[0].pageX + event.touches[1].pageX) / 2) - deltaX
      initialY = ((event.touches[0].pageY + event.touches[1].pageY) / 2) - deltaY
    }

    else if (event.touches.length === 1) {
      // Calculate where the fingers have started on the X and Y axis
      initialX = event.touches[0].pageX - deltaX
      initialY = event.touches[0].pageY - deltaY

      if (prevent) {
        panning = true
        event.stopPropagation()
      } else {
        panning = false
        clearTimeout(panningId)

        panningId = setTimeout(() => panning = true, panningTime)
      }
    }
  }

  const onTouchMove = (event) => {
    event.preventDefault()

    if (event.touches.length === 2) {
      panning = true
      event.stopPropagation()

      // Calculate the current distance between fingers
      const currentDistance = distance(event)

      // Calculate the scale change based on the initial distance and current distance
      const scaleChange = currentDistance / initialDistance

      // Update the image scale
      imageScale = Math.min(Math.max(minScale, initialScale * scaleChange), maxScale)

      // Calculate how much the fingers have moved on the X and Y axis
      deltaX = ((event.touches[0].pageX + event.touches[1].pageX) / 2) - initialX
      deltaY = ((event.touches[0].pageY + event.touches[1].pageY) / 2) - initialY

      transformImage()
      correctPosition()
    }

    else if (event.touches.length === 1 && panning) {
      if (imageScale > minScale) {
        event.stopPropagation()

        // Calculate how much the fingers have moved on the X and Y axis
        deltaX = event.touches[0].pageX - initialX
        deltaY = event.touches[0].pageY - initialY

        transformImage()
        correctPosition()
      } else {
        prevent = false
        panning = false
      }
    }

    clearTimeout(panningId)
  }

  const onTouchEnd = (event) => {
    event.preventDefault()
    prevent && event.stopPropagation()

    // Reset initial values
    initialDistance = 0
    initialScale = 1

    initialX = 0
    initialY = 0

    panning = false
  }

  image.addEventListener('touchstart', onTouchStart)
  image.addEventListener('touchmove', onTouchMove)
  image.addEventListener('touchend', onTouchEnd)
  image.addEventListener('touchcancel', onTouchEnd)

  // Don't use pointer events on touch devices
  if ('ontouchstart' in window) return

  let dragStartX = 0
  let dragStartY = 0

  let lastTimestamp = null
  let velocity = 0

  let dragging = false
  let draggingTime = 100
  let draggingId = null

  const onMouseEnter = () => {
    boxRect = container.getBoundingClientRect()
    imageRatio = image.naturalWidth / image.naturalHeight
  }

  const onWheel = (event) => {
    event.preventDefault()

    const currentTimestamp = Date.now()
    const delta = (event.deltaY || event.detail || event.wheelDelta)

    // Calculate time elapsed since the last wheel event
    const timeElapsed = currentTimestamp - (lastTimestamp || currentTimestamp)

    // Calculate velocity based on the change in wheel delta and time elapsed
    velocity = timeElapsed ? (0.1 * delta) / timeElapsed : 0

    // Update the last timestamp for the next wheel event
    lastTimestamp = currentTimestamp

    // Calculate the zoom factor based on the inverted wheel delta and velocity
    const zoomFactor = 1 + (0.0001 * Math.abs(delta) * Math.sign(delta) - velocity)

    // Update the image scale
    imageScale = Math.min(Math.max(minScale, imageScale * zoomFactor), maxScale)

    transformImage()
    correctPosition()
  }

  const onMouseDown = (event) => {
    event.preventDefault()

    onMouseEnter()

    dragStartX = event.clientX
    dragStartY = event.clientY

    dragging = false
    clearTimeout(draggingId)

    draggingId = setTimeout(() => dragging = true, draggingTime)
  }

  const onMouseUp = (event) => {
    event.preventDefault()
    prevent && event.stopPropagation()

    dragging = false
    clearTimeout(draggingId)
  }

  const onMouseMove = (event) => {
    if (dragging && imageScale > minScale) {
      event.stopPropagation()

      const clientX = event.clientX
      const clientY = event.clientY

      deltaX += (clientX - dragStartX)
      deltaY += (clientY - dragStartY)

      dragStartX = clientX
      dragStartY = clientY

      transformImage()
      correctPosition()
    }
  }

  image.addEventListener('mouseenter', onMouseEnter)
  image.addEventListener('wheel', onWheel)

  image.addEventListener('pointerdown', onMouseDown)
  image.addEventListener('pointermove', onMouseMove)
  image.addEventListener('pointerup', onMouseUp)
}
